import { useCallback, useMemo } from 'react'
import { useQueries } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { fetchFecJournalMappings } from '../query-api'
import type { FecJournalMappingWithImport } from '../types/fecJournalMappingWithImport'
import { dedupeFecJournalMappings } from '../utils/dedupeFecJournalMappings'
import { uniqueIds } from '../utils/uniqueIds'

export interface UseFetchFecJournalMappingsResult {
  /** The mappings of every import as they came, before the same journal of several imports became one row. */
  allJournalMappings: FecJournalMappingWithImport[]
  isError: boolean
  isLoading: boolean
  isRetrying: boolean
  journalMappings: FecJournalMappingWithImport[]
  retry: () => void
}

export const useFetchFecJournalMappings = (fecImportIds: string[]): UseFetchFecJournalMappingsResult => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id || ''
  const uniqueFecImportIds = useMemo(() => uniqueIds(fecImportIds), [fecImportIds])

  const queries = useQueries(
    uniqueFecImportIds.map((fecImportId) => ({
      enabled: !!organizationId && !!fecImportId,
      queryFn: async (): Promise<FecJournalMappingWithImport[]> => {
        try {
          const mappings = await fetchFecJournalMappings(organizationId, fecImportId)

          return mappings.map((mapping) => ({ ...mapping, fecImportId }))
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)

          throw new Error(`FEC journal mappings fetch failed for import ${fecImportId}: ${message}`)
        }
      },
      queryKey: [QueryKeys.FecJournalMappings, organizationId, fecImportId],
    })),
  )

  const dataUpdatedAtKey = queries.map((query) => query.dataUpdatedAt).join('|')

  const allJournalMappings = useMemo(
    () => queries.flatMap((query) => query.data || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by dataUpdatedAt snapshots
    [dataUpdatedAtKey],
  )
  const journalMappings = useMemo(() => dedupeFecJournalMappings(allJournalMappings), [allJournalMappings])

  const retry = useCallback(() => {
    for (const query of queries) {
      if (query.isError) {
        query.refetch()
      }
    }
  }, [queries])

  return {
    allJournalMappings,
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading || query.isIdle),
    isRetrying: queries.some((query) => query.isError && query.isFetching),
    journalMappings,
    retry,
  }
}
